import React from "react";
import { useNavigate } from "react-router-dom";
import usePracticeData from "../../../hooks/usePracticeData";
import { Ghost, Snowflake, Baby } from "lucide-react";

const PathCards = () => {
  const navigate = useNavigate();
  const { practiceData, showSpinner } = usePracticeData();

  const roles =
    practiceData.data?.map((item) => ({
      slug: item.slug,
      name: item.type || item.slug,
      description: item.description,
      shortDescription: item.shortDescription,
      author: item.author,
    })) || [];

  const adventBoxes = [
    {
      path: "/advent/apex-week-of-horror",
      title: "Week of Horror",
      subtitle: "8 Halloween Challenges",
      theme: "halloween",
      icon: Ghost,
      bgGradient: "from-orange-600 via-purple-900 to-black",
      glowColor: "orange-500",
      description: "Survive the nightmare",
      decorations: (
        <>
          <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-0 left-0 w-40 h-40 bg-purple-600/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "300ms" }}
          />
        </>
      ),
    },
    {
      path: "/advent/2024",
      title: "Save the Village",
      subtitle: "2024 Winter Advent",
      theme: "christmas",
      icon: Snowflake,
      bgGradient: "from-red-700 via-green-800 to-emerald-950",
      glowColor: "red-400",
      description: "A festive adventure awaits",
      decorations: (
        <>
          <div className="absolute top-2 left-2 w-3 h-3 bg-white rounded-full animate-ping" />
          <div
            className="absolute top-8 right-8 w-2 h-2 bg-red-300 rounded-full animate-ping"
            style={{ animationDelay: "150ms" }}
          />
          <div
            className="absolute bottom-4 left-8 w-2 h-2 bg-green-300 rounded-full animate-ping"
            style={{ animationDelay: "300ms" }}
          />
        </>
      ),
    },
    {
      path: "/advent/2025",
      title: "Baby's First Christmas",
      subtitle: "2025 Winter Advent",
      theme: "baby",
      icon: Baby,
      bgGradient: "from-blue-400 via-pink-300 to-purple-200",
      glowColor: "pink-400",
      description: "A magical beginning",
      decorations: (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent" />
          <div className="absolute top-0 left-1/4 w-24 h-24 bg-pink-200/20 rounded-full blur-2xl animate-pulse" />
        </>
      ),
    },
  ];

  if (showSpinner) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center pt-24 pb-12 bg-zinc-900">
      <div className="text-center space-y-6 z-10 mb-16">
        <h1 className="text-xl md:text-3xl font-medium transition-colors pb-9">
          Hands-on practice problems to help you get more experience.
        </h1>

        {/* <a href="https://tally.so/r/wo85X1">
          <span className="hover:underline text-purple-300">
            Book a mock 1:1 interview with me
          </span>
        </a> */}
      </div>

      {/* Regular practice paths */}
      <div className="max-w-6xl mx-auto px-4 w-full">
        <h2 className="text-2xl font-bold text-white mb-6 text-center tracking-tight">
          Practice Paths
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map((role) => (
            <button
              key={role.slug}
              onClick={() => navigate(`/practice/${role.slug.toLowerCase()}`)}
              className="group relative overflow-hidden bg-zinc-800/50 backdrop-blur p-8 rounded-2xl
                         transition-all duration-500 ease-out
                         hover:bg-zinc-800
                         hover:-translate-y-1
                         active:translate-y-0
                         border border-white/[0.08]
                         hover:border-white/[0.12]"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/[0.07] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <h3 className="text-white font-medium text-xl mb-3">
                {role.name}
              </h3>
              <p className="text-zinc-300 text-sm leading-relaxed">
                {role.shortDescription}
              </p>
              <p className="text-zinc-500 text-sm mt-4">{role.author}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Special Advent Calendar Boxes */}
      <div className="max-w-6xl mx-auto px-4 w-full mb-12 pt-16">
        <h2 className="text-2xl font-bold text-white mb-6 text-center tracking-tight">
          ✨ Special Events
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adventBoxes.map((box, index) => {
            const Icon = box.icon;
            return (
              <button
                key={index}
                onClick={() => navigate(box.path)}
                className="group relative overflow-hidden rounded-2xl transform transition-all duration-500 
                           hover:scale-105 hover:-translate-y-3 active:scale-100 active:translate-y-0"
                style={{
                  animation: `fadeInUp 0.6s ease-out forwards ${index * 150}ms`,
                  opacity: 0,
                }}
              >
                {/* Animated background gradient */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${box.bgGradient} 
                              transition-transform duration-700 group-hover:scale-110`}
                />

                {/* Theme decorations */}
                {box.decorations}

                {/* Content container */}
                <div className="relative p-8 flex flex-col justify-between min-h-[280px]">
                  {/* Icon and badge */}
                  <div className="flex justify-between items-start">
                    <div
                      className={`p-4 bg-black/30 backdrop-blur-sm rounded-2xl border border-white/20
                                  transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-12
                                  shadow-[0_0_20px_rgba(255,255,255,0.1)]`}
                    >
                      <Icon className="w-10 h-10 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
                    </div>

                    <div
                      className={`px-3 py-1 bg-black/40 backdrop-blur-md rounded-full border border-white/30
                                  text-xs font-bold text-white tracking-wider`}
                    >
                      {box.subtitle.includes("2024")
                        ? "2024"
                        : box.subtitle.includes("2025")
                        ? "2025"
                        : "Halloween"}
                    </div>
                  </div>

                  {/* Text content */}
                  <div className="space-y-3">
                    <h3
                      className="text-3xl font-black text-white tracking-tight leading-tight
                                 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]
                                 transform transition-transform duration-300 group-hover:translate-x-1"
                    >
                      {box.title}
                    </h3>
                    <p className="text-lg font-semibold text-white/90 tracking-wide">
                      {box.subtitle}
                    </p>
                    <p
                      className="text-sm text-white/80 font-medium italic
                                 transform transition-all duration-300 opacity-0 
                                 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2"
                    >
                      {box.description}
                    </p>
                  </div>

                  {/* Hover indicator */}
                  <div
                    className="absolute bottom-4 right-4 transform transition-all duration-300 
                                  opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0"
                  >
                    <div className="flex items-center gap-2 text-white/90 text-sm font-bold">
                      <span>ENTER</span>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Border glow effect */}
                <div
                  className="absolute inset-0 border-2 border-white/0 rounded-2xl 
                             group-hover:border-white/30 transition-all duration-300
                             shadow-[0_0_30px_rgba(0,0,0,0.3)] 
                             group-hover:shadow-[0_0_50px_rgba(255,255,255,0.2)]"
                />
              </button>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PathCards;
